import { useStableCallback } from "@business-finland/wif-ui-lib";
import { useCallback } from "react";

import { localesEmployers } from "../../../constants/locale";
import useMutation from "../../../hooks/useMutation";
import { CompanyFormSubmitError } from "../../../util/companies-utils";
import {
  type CompanyFormDispatch,
  type CompanyFormState,
  companyFormActions,
} from "./store";

const companyLogoFieldName = "companyLogo";

export default function useSubmit(
  dispatch: CompanyFormDispatch,
  state: CompanyFormState
) {
  const { mutate } = useMutation<CompanyIdValidationResponse | string>(
    "/api/companies/form-submit/",
    {
      onLoading: useStableCallback(() =>
        dispatch(companyFormActions.startLoading())
      ),
      onSuccess: useStableCallback((data) => {
        if (data && typeof data === "string" && data !== "OK") {
          return dispatch(
            companyFormActions.setErrorMessage(data as CompanyFormSubmitError)
          );
        } else {
          dispatch(companyFormActions.submit());
        }
      }),
      onError: useStableCallback(() => {
        dispatch(companyFormActions.stopLoading());
      }),
    }
  );

  return useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      applyCompanyFormState(formData, state);

      const logoFile = formData.get(companyLogoFieldName);
      if (logoFile instanceof File && logoFile.size === 0) {
        formData.delete(companyLogoFieldName);
      }
      if (
        !formData.get(companyLogoFieldName) &&
        state.companyLogo instanceof File &&
        state.companyLogo.size > 0
      ) {
        formData.set(companyLogoFieldName, state.companyLogo);
      }

      mutate({ method: "POST", body: formData });
    },
    [mutate, state]
  );
}

function applyCompanyFormState(
  formData: FormData,
  state: CompanyFormState
): void {
  if (state.companyId) formData.set("companyId", state.companyId);
  if (state.companyName) formData.set("companyName", state.companyName);
  if (state.companyEmail) formData.set("companyEmail", state.companyEmail);
  if (state.companyPhone) {
    formData.set("companyPhone", state.companyPhone);
    formData.delete("companyPhone-cc");
  }

  if (state.companyServices.length) {
    formData.delete("companyServices");
    state.companyServices.forEach((service) => {
      if (service.value) formData.append("companyServices", service.value);
    });
  }

  localesEmployers.forEach((locale) => {
    const description = state.companyDescription[locale];
    if (description) formData.set(`companyDescription-${locale}`, description);
    const website = state.companyWebsite[locale];
    if (website) formData.set(`companyWebsite-${locale}`, website);
  });

  if (state.companyTerms) formData.set("companyTerms", "on");
}

type CompanyIdValidationResponse = {
  companyId: string;
  valid: boolean;
};
